/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
/**
 * Contextual help content, keyed by Vue route name.
 *
 * Each route can expose a list of tips displayed at the top of the
 * HelpWidget panel under "Sur cette page". Add a new entry here when a
 * page deserves in-context guidance; the widget gracefully hides the
 * section when the current route has no entry.
 *
 * Shape: { [routeName: string]: Tip[] }
 * Tip:   { icon, title, description }
 */
const help = {
    // --- News ---
    News: [
        {
            icon: 'fal fa-filter',
            title: 'Filtrer vos actualités',
            description:
                'Combinez les filtres statut (brouillon / publié) et catégorie pour retrouver rapidement un article. La recherche textuelle porte sur le titre.'
        },
        {
            icon: 'fal fa-bolt',
            title: 'Actions au bout de la ligne',
            description:
                'Chaque ligne propose voir, éditer, publier ou dépublier et supprimer — sans quitter la liste. Le titre reste cliquable pour ouvrir l’article.'
        },
        {
            icon: 'fal fa-tags',
            title: 'Organiser en catégories',
            description:
                'Le bouton « Gérer les catégories » en haut à droite ouvre la gestion — utile pour regrouper et classer vos actualités.'
        }
    ],

    NewsCreate: [
        {
            icon: 'fal fa-language',
            title: 'FR obligatoire, EN optionnel',
            description:
                'La version française doit être renseignée pour publier. Les pastilles colorées dans les onglets vous montrent la complétion de chaque langue.'
        },
        {
            icon: 'fal fa-image',
            title: 'Image de couverture',
            description:
                'Acceptée jusqu’à 5 Mo (JPEG / PNG / WebP). Elle sert dans la liste publique et dans les partages sociaux.'
        },
        {
            icon: 'fal fa-save',
            title: 'Brouillon vs Publier',
            description:
                '« Brouillon » enregistre sans rendre l’article public. « Publier » enregistre et rend immédiatement disponible sur le site public.'
        }
    ],

    NewsEdit: [
        {
            icon: 'fal fa-language',
            title: 'Versions FR et EN',
            description:
                'Passez d’un onglet à l’autre pour éditer chaque langue. Les pastilles indiquent la complétion.'
        },
        {
            icon: 'fal fa-eye-slash',
            title: 'Dépublier un article',
            description:
                'Si l’article est publié, le bouton « Dépublier » le repasse en brouillon — il disparaît alors du site public sans être supprimé.'
        },
        {
            icon: 'fal fa-trash',
            title: 'Suppression = soft delete',
            description:
                'La suppression est réversible côté back-office sur demande. L’article cesse immédiatement d’être visible côté public.'
        }
    ],

    NewsView: [
        {
            icon: 'fal fa-eye',
            title: 'Aperçu de l’article',
            description:
                'Cette page affiche l’article tel qu’il sera lu : couverture, catégorie, auteur et date de publication compris.'
        },
        {
            icon: 'fal fa-paper-plane',
            title: 'Publier depuis l’aperçu',
            description:
                'Les boutons en haut à droite permettent de publier un brouillon, de dépublier un article en ligne ou de basculer directement en édition.'
        }
    ],

    NewsCategories: [
        {
            icon: 'fal fa-tag',
            title: 'Nom FR obligatoire',
            description:
                'Le nom anglais est optionnel ; s’il est vide, la liste publique affichera le nom français pour les visiteurs en EN.'
        },
        {
            icon: 'fal fa-toggle-on',
            title: 'Activer / désactiver',
            description:
                'Une catégorie inactive reste liée aux articles existants mais n’est plus proposée à la création d’un nouvel article.'
        }
    ],

    // --- Tickets ---
    Data: [
        {
            icon: 'fal fa-filter',
            title: 'Filtres simples puis avancés',
            description:
                'Recherche, formulaire, statut et priorité sont affichés d’office. Le bouton « Plus » déplie les filtres avancés : responsable assigné et fenêtre de dates.'
        },
        {
            icon: 'fal fa-eye',
            title: 'Aperçu rapide sans quitter la liste',
            description:
                'L’icône œil en fin de ligne ouvre une fenêtre d’aperçu avec les infos essentielles du ticket, ses réponses et ses pièces jointes.'
        },
        {
            icon: 'fal fa-bell',
            title: 'Relancer un responsable',
            description:
                'L’icône cloche envoie une notification au responsable assigné — utile quand un ticket stagne. Statut et priorité se modifient aussi directement depuis la liste.'
        },
        {
            icon: 'fal fa-file-export',
            title: 'Exporter en tableur',
            description:
                'Le bouton « Export » de la barre du haut propose d’exporter tous les tickets ou uniquement ceux correspondant à vos filtres actifs. Le fichier est téléchargé au format xlsx.'
        }
    ],

    Post: [
        {
            icon: 'fal fa-comments',
            title: 'Échanges avec le citoyen',
            description:
                'La zone de messages est visible par le citoyen. Les modèles de réponse pré-remplissent le message en un clic et vous pouvez joindre des fichiers à l’envoi.'
        },
        {
            icon: 'fal fa-lock',
            title: 'Notes internes et mentions',
            description:
                'Les notes internes restent invisibles pour le citoyen. Tapez « @ » pour mentionner un collègue : il reçoit une notification.'
        },
        {
            icon: 'fal fa-flag',
            title: 'Statut, priorité et relance',
            description:
                'Les pastilles statut et priorité de l’en-tête se modifient en un clic, l’icône cloche relance le responsable, et tout est tracé dans l’historique du ticket.'
        },
        {
            icon: 'fal fa-file-pdf',
            title: 'Export PDF et référence',
            description:
                'L’icône de téléchargement génère un PDF du ticket, et l’icône de copie place sa référence dans le presse-papier pour la coller dans un email.'
        }
    ],

    Kanban: [
        {
            icon: 'fal fa-clipboard-list',
            title: 'Choisissez d’abord un formulaire',
            description:
                'Le tableau se construit sur les statuts d’un formulaire : sélectionnez-le, puis utilisez « Retour » pour en changer.'
        },
        {
            icon: 'fal fa-arrows',
            title: 'Glisser-déposer',
            description:
                'Faites glisser un ticket d’une colonne à l’autre pour changer son statut. L’enregistrement est immédiat, sans validation supplémentaire.'
        },
        {
            icon: 'fal fa-filter',
            title: 'Filtres respectés',
            description:
                'Les filtres sélectionnés en haut s’appliquent sur toutes les colonnes simultanément.'
        }
    ],

    TicketMap: [
        {
            icon: 'fal fa-map-marker-alt',
            title: 'Ticket cliquable',
            description:
                'Cliquez sur un marqueur pour ouvrir un aperçu du ticket. La puce colorée reflète son statut actuel.'
        },
        {
            icon: 'fal fa-layer-group',
            title: 'Clusters de proximité',
            description:
                'Les tickets regroupés par zone apparaissent en cluster numéroté — zoomez pour les éclater individuellement.'
        },
        {
            icon: 'fal fa-filter',
            title: 'Seuls les tickets localisés',
            description:
                'La carte n’affiche que les tickets dont le formulaire comporte un champ de localisation renseigné. Les filtres du haut s’y appliquent comme sur la liste.'
        }
    ],

    // --- Form builder ---
    CustomFormBuilder: [
        {
            icon: 'fal fa-route',
            title: 'Navigation par étape',
            description:
                'Le stepper en haut vous permet de naviguer entre les étapes du builder — vos modifications sont conservées entre les étapes.'
        },
        {
            icon: 'fal fa-cubes',
            title: 'Palette, canvas et configuration',
            description:
                'Glissez un champ depuis la palette de gauche vers le canvas central, puis cliquez dessus : son panneau de configuration s’ouvre à droite (libellé, obligatoire, options…).'
        },
        {
            icon: 'fal fa-language',
            title: 'Contenus FR et EN',
            description:
                'Le sélecteur de langue bascule les libellés des étapes et des champs. Une traduction EN absente retombe automatiquement sur le texte français côté public.'
        },
        {
            icon: 'fal fa-code-branch',
            title: 'Pensez au déploiement',
            description:
                'Enregistrer ne suffit pas : un formulaire n’est accessible qu’une fois déployé. Créez son déploiement depuis l’onglet « Déploiements » de la fiche formulaire.'
        }
    ],

    // --- Company admin ---
    CompanyRoles: [
        {
            icon: 'fal fa-user-tag',
            title: 'Vos rôles, vos règles',
            description:
                'Créez autant de rôles que votre organisation en a besoin (Chef de service voirie, Accueil, Propreté…). Vous saisissez le nom affiché ; l’identifiant technique est généré tout seul.'
        },
        {
            icon: 'fal fa-clone',
            title: 'Dupliquer plutôt que repartir de zéro',
            description:
                'L’icône « copie » crée un rôle reprenant toutes les permissions de l’original — le plus rapide pour décliner une variante (un adjoint, une équipe voisine).'
        },
        {
            icon: 'fal fa-shield-check',
            title: 'Attribuer les permissions',
            description:
                'Le bouton « Permissions » ouvre la page dédiée au rôle. Un rôle créé de zéro n’a aucun droit ; un rôle dupliqué hérite de ceux de son modèle.'
        },
        {
            icon: 'fal fa-people-arrows',
            title: 'Supprimer sans laisser d’orphelins',
            description:
                'À la suppression, choisissez le rôle qui reprend les utilisateurs concernés. Sans réaffectation, ils se retrouvent sans rôle ni permission.'
        }
    ],

    CompanyRolePermissions: [
        {
            icon: 'fal fa-layer-group',
            title: 'Permissions groupées par module',
            description:
                'Les interrupteurs sont regroupés par feature (Signalements, Actualités, Formulaires…). Le compteur de la colonne de gauche indique les droits accordés sur chaque module.'
        },
        {
            icon: 'fal fa-toggle-on',
            title: 'Tout activer d’un coup',
            description:
                'L’interrupteur « Tout » en haut du module accorde ou retire l’ensemble de ses permissions. Les lignes modifiées restent surlignées jusqu’à l’enregistrement.'
        },
        {
            icon: 'fal fa-lock',
            title: 'Permissions verrouillées',
            description:
                'Un cadenas signale une permission que votre propre compte ne peut pas accorder : elle reste visible mais son interrupteur est figé.'
        },
        {
            icon: 'fal fa-sync',
            title: 'Prise en compte côté utilisateur',
            description:
                'Un utilisateur déjà connecté récupère ses nouvelles permissions au rechargement de la page — demandez-lui de rafraîchir si le changement est urgent.'
        }
    ],

    CompanySettings: [
        {
            icon: 'fal fa-building',
            title: 'Identité de la commune',
            description:
                'Le nom, le logo et les coordonnées définis dans l’onglet « Général » sont repris dans les communications automatiques envoyées aux citoyens.'
        },
        {
            icon: 'fal fa-envelope',
            title: 'Emails et WhatsApp',
            description:
                'Les onglets dédiés configurent l’expéditeur des emails et le canal WhatsApp. Testez toujours après modification : un paramétrage invalide bloque les notifications.'
        },
        {
            icon: 'fal fa-key',
            title: 'Tokens d’export',
            description:
                'L’onglet « Tokens », réservé aux administrateurs, gère les jetons utilisés par les exports automatisés. La colonne « Dernière utilisation » aide à repérer un jeton dormant.'
        }
    ],

    CompanyUsers: [
        {
            icon: 'fal fa-user-plus',
            title: 'Créer un utilisateur',
            description:
                'Les nouveaux utilisateurs reçoivent un email d’invitation. Le rôle attribué détermine leurs permissions (voir la page Rôles).'
        },
        {
            icon: 'fal fa-user-secret',
            title: 'Prendre la main (impersonation)',
            description:
                'L’icône agent secret ouvre la plateforme telle que la voit cet utilisateur — idéal pour reproduire un problème signalé. Un bandeau rappelle que vous êtes en impersonation.'
        },
        {
            icon: 'fal fa-user-lock',
            title: 'Désactiver plutôt que supprimer',
            description:
                'Désactiver un utilisateur conserve l’historique de ses actions tout en bloquant son accès. Préférez cette option à la suppression.'
        }
    ],

    CompanyCitizens: [
        {
            icon: 'fal fa-search',
            title: 'Retrouver un citoyen',
            description:
                'La recherche porte sur le nom, l’email et le téléphone. Le filtre « Rôle local » isole les citoyens, agents techniques et comités de quartier.'
        },
        {
            icon: 'fal fa-user-tag',
            title: 'Le rôle local pilote l’accès',
            description:
                'Le rôle local détermine quels formulaires publics le citoyen peut voir et remplir — c’est le même réglage que « Disponible en public pour » côté formulaire.'
        },
        {
            icon: 'fal fa-at',
            title: 'Nom en italique = email',
            description:
                'Quand un compte n’a pas de nom renseigné, la liste affiche son email en italique grisé pour montrer qu’il ne fait que remplacer le nom.'
        }
    ],

    CreateCompanyCitizen: [
        {
            icon: 'fal fa-user-plus',
            title: 'Compte créé pour le citoyen',
            description:
                'Renseignez identité, email et téléphone, puis définissez un mot de passe (8 caractères minimum) à communiquer au citoyen — il pourra le changer depuis son compte.'
        },
        {
            icon: 'fal fa-lock',
            title: 'Le rôle « Citoyen » est grisé',
            description:
                'Pour des raisons de structuration, un compte simple citoyen se crée uniquement par l’inscription du citoyen lui-même. Seuls les autres rôles locaux s’attribuent ici.'
        },
        {
            icon: 'fal fa-id-card',
            title: 'Un rôle par carte',
            description:
                'Chaque carte de rôle décrit ce qu’il autorise. Le rôle choisi ici détermine les formulaires publics auxquels le compte aura accès.'
        },
        {
            icon: 'fal fa-phone',
            title: 'Téléphone au format international',
            description:
                'Saisissez le numéro avec son indicatif, comme +32470123456 : c’est ce format qui permet l’envoi des notifications SMS et WhatsApp.'
        },
        {
            icon: 'fal fa-exclamation-circle',
            title: 'Email déjà utilisé ?',
            description:
                'Les erreurs remontées par le serveur s’affichent sous le champ concerné — le plus fréquent étant un email déjà rattaché à un compte existant.'
        }
    ],

    CompanyWebhooks: [
        {
            icon: 'fal fa-bolt',
            title: 'Un endpoint HTTPS par webhook',
            description:
                'Chaque webhook déclare une URL qui recevra en POST les événements de votre organisation (nouveau ticket, changement de statut, message citoyen…).'
        },
        {
            icon: 'fal fa-lock',
            title: 'Secret de signature',
            description:
                'Le secret partagé sert à signer chaque payload envoyé. Vérifiez cette signature côté réception pour vous assurer que la requête vient bien de BougeMaVille.'
        },
        {
            icon: 'fal fa-redo',
            title: 'Répondez vite en 2xx',
            description:
                'Votre endpoint doit répondre rapidement avec un code 2xx. Traitez le contenu en asynchrone plutôt que de faire attendre la requête.'
        }
    ],

    BackofficeInfos: [
        {
            icon: 'fal fa-sitemap',
            title: 'Vue d’ensemble',
            description:
                'Cette page résume votre configuration : types de formulaires, statuts, RACI, déploiements et champs. Utile pour un audit rapide.'
        },
        {
            icon: 'fal fa-search',
            title: 'Repérer les trous',
            description:
                'Servez-vous-en pour détecter un formulaire sans déploiement, sans responsable ou sans statut — les trois oublis les plus fréquents.'
        }
    ],

    // --- Tickets (wizards) ---
    FormChoice: [
        {
            icon: 'fal fa-layer-group',
            title: 'Choix du formulaire',
            description:
                'Les formulaires sont regroupés par type de demande. Cliquez sur une carte pour lancer le wizard de création du ticket correspondant.'
        },
        {
            icon: 'fal fa-eye-slash',
            title: 'Un formulaire manque ?',
            description:
                'Seuls les formulaires accessibles à votre commune apparaissent ici. Vérifiez son type et ses droits depuis la page Formulaires s’il est absent.'
        }
    ],

    CreatePost: [
        {
            icon: 'fal fa-route',
            title: 'Navigation par étape',
            description:
                'Le wizard vous guide étape par étape. Les champs marqués d’un astérisque sont obligatoires pour passer à la suite.'
        },
        {
            icon: 'fal fa-map-marker-alt',
            title: 'Localisation',
            description:
                'Si le formulaire a une restriction géographique, choisissez une adresse à l’intérieur de la zone autorisée — sinon le bouton « Suivant » restera inactif.'
        },
        {
            icon: 'fal fa-paperclip',
            title: 'Pièces jointes',
            description:
                'Taille max par fichier définie par le formulaire. Plusieurs fichiers peuvent être ajoutés sur un même champ upload.'
        }
    ],

    EditPost: [
        {
            icon: 'fal fa-save',
            title: 'Modifications immédiates',
            description:
                'Les changements sont enregistrés dès validation du wizard. L’historique du ticket garde la trace des modifications.'
        },
        {
            icon: 'fal fa-user-check',
            title: 'Le citoyen n’est pas prévenu',
            description:
                'Corriger une réponse ne notifie pas l’auteur du ticket. Pour l’informer, écrivez-lui un message depuis la fiche du ticket.'
        }
    ],

    // --- Stats & performance ---
    Statistiques: [
        {
            icon: 'fal fa-chart-pie',
            title: 'Un tableau de bord configurable',
            description:
                'Les indicateurs affichés ici viennent de la configuration de votre commune. Tant qu’aucun n’est sélectionné, la page reste vide.'
        },
        {
            icon: 'fal fa-calendar-alt',
            title: 'Changer de période',
            description:
                'Le sélecteur en haut à droite recalcule tous les widgets sur 7 jours, 30 jours, 90 jours ou depuis le début de l’année.'
        },
        {
            icon: 'fal fa-cog',
            title: 'Choisir les indicateurs',
            description:
                'La roue crantée, réservée aux administrateurs, ouvre la configuration du tableau de bord pour ajouter ou retirer des indicateurs.'
        }
    ],

    StatisticsConfig: [
        {
            icon: 'fal fa-check-square',
            title: 'Sélection des indicateurs',
            description:
                'Cochez les indicateurs du catalogue à afficher. « Tout sélectionner » / « Tout désélectionner » accélèrent la mise au point.'
        },
        {
            icon: 'fal fa-chart-bar',
            title: 'Chaque indicateur a son affichage',
            description:
                'Carte KPI, jauge, camembert, histogramme, courbe ou aire : le type d’affichage est propre à l’indicateur et détermine sa place dans la grille.'
        },
        {
            icon: 'fal fa-users',
            title: 'Configuration partagée',
            description:
                'L’enregistrement s’applique au tableau de bord de toute la commune, pas seulement au vôtre.'
        },
        {
            icon: 'fal fa-exclamation-triangle',
            title: 'Ne validez pas une sélection vide',
            description:
                'Un avertissement apparaît si plus aucun indicateur n’est coché : enregistrer dans cet état laisse le tableau de bord entièrement vide.'
        },
        {
            icon: 'fal fa-ruler',
            title: 'Lisez l’unité avant de cocher',
            description:
                'Chaque carte annonce son type d’affichage et son unité (nombre, minutes, heures, pourcentage, ratio) — de quoi éviter de comparer des délais avec des volumes.'
        },
        {
            icon: 'fal fa-save',
            title: 'Rien n’est appliqué avant « Enregistrer »',
            description:
                'La barre de progression suit votre sélection en direct, mais « Annuler » vous ramène au tableau de bord sans rien modifier.'
        }
    ],

    StatistiquesByFormType: [
        {
            icon: 'fal fa-chart-line',
            title: 'Volumétrie du type de formulaire',
            description:
                'Répartition des tickets de ce type de formulaire : ouverts / résolus, délais de réponse, assignations et statuts.'
        },
        {
            icon: 'fal fa-calendar-week',
            title: 'Par mois et par semaine',
            description:
                'Les histogrammes du bas comparent le volume de tickets mois par mois puis semaine par semaine, pour repérer les pics d’activité.'
        }
    ],

    Performances: [
        {
            icon: 'fal fa-stopwatch',
            title: 'Réactivité de vos équipes',
            description:
                'Les cartes résument les volumes de réponses, les tickets ayant reçu une réponse et les délais de traitement observés.'
        },
        {
            icon: 'fal fa-exclamation-triangle',
            title: 'Tickets clôturés sans réponse',
            description:
                'Cet indicateur est le plus parlant : il compte les tickets terminés sans qu’aucun message n’ait été adressé au citoyen.'
        }
    ],

    // --- Home / dashboard ---
    Home: [
        {
            icon: 'fal fa-tachometer-alt',
            title: 'Tableau de bord',
            description:
                'Vue synthétique des tickets récents et des indicateurs clés. Les cards sont cliquables — elles mènent à la liste filtrée correspondante.'
        },
        {
            icon: 'fal fa-calendar-alt',
            title: 'Vos chiffres, sur la période choisie',
            description:
                'Le sélecteur de période en haut à droite recalcule les compteurs, qui distinguent l’activité de toute la commune de celle de vos propres tickets.'
        },
        {
            icon: 'fal fa-bars',
            title: 'Sidebar de navigation',
            description:
                'Tous les modules (Tickets, Formulaires, Actualités, Équipe…) sont accessibles via la sidebar à gauche. En mode replié, survolez une icône pour faire apparaître son sous-menu.'
        }
    ],

    Notifications: [
        {
            icon: 'fal fa-inbox',
            title: 'Toutes ou non lues',
            description:
                'Les deux onglets filtrent la liste. Ouvrir une notification la marque comme lue et vous emmène directement sur le ticket concerné.'
        },
        {
            icon: 'fal fa-check-double',
            title: 'Tout marquer comme lu',
            description:
                'Le bouton apparaît dès qu’il reste des notifications non lues — pratique pour repartir d’une boîte propre après une absence.'
        }
    ],

    Documentation: [
        {
            icon: 'fal fa-code-branch',
            title: 'Historique des versions',
            description:
                'Cette page liste les mises à jour de la plateforme, de la plus récente à la plus ancienne : vous y retrouvez les nouveautés et les correctifs livrés.'
        },
        {
            icon: 'fal fa-life-ring',
            title: 'Besoin d’aide humaine ?',
            description:
                'Si le changelog ne répond pas à votre question, utilisez « Signaler un incident » ci-dessous pour joindre le support.'
        }
    ],

    // --- Response templates ---
    Templates: [
        {
            icon: 'fal fa-reply',
            title: 'Modèles de réponse',
            description:
                'Les modèles pré-remplissent les messages envoyés aux citoyens depuis la zone d’échanges d’un ticket. Utile pour les réponses récurrentes.'
        },
        {
            icon: 'fal fa-code',
            title: 'Variables dynamiques',
            description:
                'Les modèles peuvent inclure des variables (nom du citoyen, référence du ticket) automatiquement substituées à l’envoi.'
        },
        {
            icon: 'fal fa-pen',
            title: 'Toujours relire avant envoi',
            description:
                'Un modèle inséré dans un message reste modifiable : ajustez-le au cas du citoyen avant d’envoyer.'
        }
    ],

    CreateTemplate: [
        {
            icon: 'fal fa-language',
            title: 'Traduction FR / EN',
            description:
                'Le modèle est défini par langue. Si un modèle EN est absent, la version FR est utilisée en fallback.'
        }
    ],

    EditTemplate: [
        {
            icon: 'fal fa-language',
            title: 'Traduction FR / EN',
            description:
                'Modifier un modèle impacte tous les futurs envois qui l’utilisent. Les messages déjà envoyés restent inchangés.'
        }
    ],

    // --- Events & projects ---
    EventsList: [
        {
            icon: 'fal fa-calendar-alt',
            title: 'Événements et projets',
            description:
                'Les deux types cohabitent dans la même liste : un événement est daté, un projet s’étale dans le temps. Le badge de couleur les distingue.'
        },
        {
            icon: 'fal fa-globe',
            title: 'Bascule tableau / carte',
            description:
                'Le sélecteur en haut à droite affiche la même sélection sous forme de tableau ou sur une carte. Seuls les éléments localisés apparaissent sur la carte.'
        },
        {
            icon: 'fal fa-filter',
            title: 'Recherche et filtres',
            description:
                'Filtrez par type et par statut (brouillon, publié, annulé), ou cherchez directement par titre.'
        },
        {
            icon: 'fal fa-eye-slash',
            title: 'Trois statuts, trois visibilités',
            description:
                'Brouillon : visible du back-office uniquement. Publié : visible des citoyens. Annulé : conservé et affiché comme annulé, jamais supprimé.'
        },
        {
            icon: 'fal fa-star',
            title: 'Mise en avant',
            description:
                'Le badge étoile signale les éléments cochés « Mettre en avant » : ce sont ceux qui remontent en tête côté public.'
        }
    ],

    EventCreate: [
        {
            icon: 'fal fa-project-diagram',
            title: 'Choisissez le type en premier',
            description:
                'Événement ou projet : ce choix conditionne l’affichage côté citoyen. Le titre est le seul champ réellement obligatoire.'
        },
        {
            icon: 'fal fa-map-marker-alt',
            title: 'Localisation sur la carte',
            description:
                'Recherchez une adresse, cliquez directement sur la carte ou utilisez « Me localiser ». Sans localisation, l’élément n’apparaîtra pas dans la vue carte.'
        },
        {
            icon: 'fal fa-image',
            title: 'Couverture et mise en avant',
            description:
                'L’image de couverture accepte jusqu’à 5 Mo. La case « Mettre en avant » place l’élément en tête de liste côté public.'
        },
        {
            icon: 'fal fa-clock',
            title: 'Dates libres',
            description:
                'Début et fin sont facultatifs : laissez la fin vide pour un projet sans échéance connue, ou les deux vides pour une initiative permanente.'
        },
        {
            icon: 'fal fa-paper-plane',
            title: 'Enregistrer ne publie pas',
            description:
                'Le builder ne comporte pas de réglage de statut : après enregistrement, ouvrez la fiche de l’élément et utilisez « Publier » pour le rendre visible des citoyens.'
        }
    ],

    EventView: [
        {
            icon: 'fal fa-paper-plane',
            title: 'Publier, dépublier, annuler',
            description:
                'Un brouillon reste invisible pour les citoyens. « Annuler » conserve l’élément et son historique, tout en le signalant comme annulé.'
        },
        {
            icon: 'fal fa-thumbs-up',
            title: 'Réactions des citoyens',
            description:
                'La barre de réactions en bas de page totalise les avis laissés par les citoyens — un bon indicateur de l’intérêt suscité.'
        },
        {
            icon: 'fal fa-map',
            title: 'Localisation affichée',
            description:
                'L’adresse et la carte n’apparaissent que si une localisation a été définie. Ajoutez-la via « Modifier » pour que l’élément remonte aussi dans la vue carte.'
        },
        {
            icon: 'fal fa-history',
            title: 'Annuler plutôt que supprimer',
            description:
                'Un élément annulé reste consultable avec son historique et signale clairement aux citoyens que la date ne tient plus.'
        }
    ],

    // --- Forms ---
    FormTypes: [
        {
            icon: 'fal fa-layer-group',
            title: 'Types de formulaires',
            description:
                'Les types regroupent vos formulaires (propreté, voirie, demandes administratives…). Un type inactif masque ses formulaires dans la navigation.'
        },
        {
            icon: 'fal fa-sitemap',
            title: 'Les types pilotent les vues',
            description:
                'Liste, kanban, carte et statistiques peuvent être filtrés par type : bien découper vos types rend ces vues beaucoup plus lisibles.'
        }
    ],

    Forms: [
        {
            icon: 'fal fa-poll',
            title: 'Vos formulaires',
            description:
                'Cette liste regroupe tous les formulaires créés. Le bouton « Nouveau formulaire » lance le builder ; le filtre à côté restreint la liste à un type.'
        },
        {
            icon: 'fal fa-code-branch',
            title: 'Créé ne veut pas dire accessible',
            description:
                'Un formulaire n’est disponible qu’une fois déployé. Ouvrez sa fiche puis l’onglet « Déploiements » pour vérifier qu’il en possède au moins un.'
        },
        {
            icon: 'fal fa-pen',
            title: 'Éditer sans casser l’existant',
            description:
                'Modifier un formulaire n’affecte pas les tickets déjà créés : ils conservent les réponses enregistrées avec l’ancienne version.'
        }
    ],

    FormTypeSelect: [
        {
            icon: 'fal fa-layer-group',
            title: 'Sélectionnez un type',
            description:
                'Le type choisi détermine la catégorie dans laquelle votre formulaire apparaîtra pour les citoyens.'
        }
    ],

    FormTypeDetail: [
        {
            icon: 'fal fa-info-circle',
            title: 'Détail du type',
            description:
                'Configurez ici le nom, l’icône et les statuts applicables aux tickets issus de ce type de formulaire.'
        },
        {
            icon: 'fal fa-list',
            title: 'Formulaires rattachés',
            description:
                'La liste du bas recense les formulaires de ce type et donne un accès direct à leur fiche ou à leur édition.'
        }
    ],

    // --- My profile ---
    MyProfile: [
        {
            icon: 'fal fa-user-edit',
            title: 'Vos informations',
            description:
                'Le bouton « Modifier » ouvre le panneau d’édition : nom, email et changement de mot de passe.'
        },
        {
            icon: 'fal fa-bell',
            title: 'Préférences de notification',
            description:
                'Choisissez par canal les événements pour lesquels vous souhaitez être prévenu. Le réglage ne vaut que pour votre compte.'
        }
    ],

    // --- Super-admin ---
    AdminCompanies: [
        {
            icon: 'fal fa-building',
            title: 'Communes du réseau',
            description:
                'Liste de toutes les communes de la plateforme. L’icône œil ouvre la fiche d’une commune, le crayon son édition.'
        },
        {
            icon: 'fal fa-exchange',
            title: 'Changer de commune active',
            description:
                'Le sélecteur de compagnie dans la barre du haut bascule tout le back-office sur la commune choisie. Pour voir la plateforme avec les yeux d’un agent, passez plutôt par l’impersonation depuis la liste des utilisateurs.'
        },
        {
            icon: 'fal fa-plus-circle',
            title: 'Ajouter une commune',
            description:
                'La création d’une nouvelle commune initialise automatiquement les rôles et permissions par défaut.'
        }
    ],

    AdminNews: [
        {
            icon: 'fal fa-bullhorn',
            title: 'Messages de sensibilisation',
            description:
                'Ces messages sont diffusés au-delà d’une seule commune. Réservés aux super-admins, ils sont distincts des actualités locales.'
        },
        {
            icon: 'fal fa-paper-plane',
            title: 'Brouillon puis publication',
            description:
                'Les actions de la ligne permettent de publier, dépublier ou supprimer sans ouvrir le message. Un brouillon n’est visible de personne.'
        }
    ],

    AdminNewsCreate: [
        {
            icon: 'fal fa-pen-alt',
            title: 'Rédiger le message',
            description:
                'Titre, contenu et image de couverture se remplissent comme pour une actualité. « Enregistrer le brouillon » permet de revenir dessus plus tard.'
        },
        {
            icon: 'fal fa-eye',
            title: 'Portée large',
            description:
                'Relisez attentivement avant publication : un message de sensibilisation touche l’ensemble des destinataires concernés, pas une seule commune.'
        }
    ],

    AdminNewsEdit: [
        {
            icon: 'fal fa-sync',
            title: 'Mise à jour immédiate',
            description:
                'Les modifications d’un message publié sont visibles dès l’enregistrement. Pour le retirer temporairement, utilisez « Dépublier ».'
        }
    ]
}

// --- Extra content ---

help.UserProfile = [
    {
        icon: 'fal fa-id-badge',
        title: 'Profil complet',
        description:
            'Retrouvez ici l’activité de l’utilisateur (tickets créés, tickets pris en charge, commentaires) en plus de ses informations personnelles.'
    },
    {
        icon: 'fal fa-shield-alt',
        title: 'Rôle & permissions',
        description:
            'Le rôle indiqué ici détermine ce que l’utilisateur peut voir et faire. Les permissions de ce rôle se règlent depuis la page Rôles.'
    },
    {
        icon: 'fal fa-history',
        title: 'Fil d’activité',
        description:
            'La timeline retrace les dernières actions de l’utilisateur — utile pour comprendre qui a modifié quoi sur un ticket.'
    }
]

help.Form = [
    {
        icon: 'fal fa-poll',
        title: 'La fiche du formulaire',
        description:
            'Cinq onglets : caractéristiques, responsables (RACI), déploiements, statuts et paramétrage. Les champs et étapes se modifient dans le builder.'
    },
    {
        icon: 'fal fa-code-branch',
        title: 'Onglet Déploiements',
        description:
            'C’est ici qu’on rend le formulaire accessible : « Créer un déploiement » le publie vers un portail (site web, application mobile). Sans déploiement, personne ne peut le remplir.'
    },
    {
        icon: 'fal fa-users',
        title: 'Responsables par défaut',
        description:
            'Les RACI définis ici sont appliqués automatiquement à chaque nouveau ticket issu de ce formulaire.'
    }
]

help.EditForm = [
    {
        icon: 'fal fa-pencil',
        title: 'Édition du formulaire',
        description:
            'Vous reprenez le builder sur un formulaire existant : informations générales, étapes et champs sont tous modifiables, étape par étape.'
    },
    {
        icon: 'fal fa-shield-check',
        title: 'Les tickets existants sont préservés',
        description:
            'Supprimer un champ n’efface pas les réponses déjà collectées : elles restent consultables sur les tickets créés avant la modification.'
    }
]

help.FormWizard = [
    {
        icon: 'fal fa-route',
        title: 'Navigation par étape',
        description:
            'Le wizard vous guide étape par étape. Les champs marqués d’un astérisque sont obligatoires pour passer à la suite.'
    },
    {
        icon: 'fal fa-map-marker-alt',
        title: 'Localisation',
        description:
            'Si le formulaire a une restriction géographique, choisissez une adresse à l’intérieur de la zone autorisée — sinon le bouton « Suivant » restera inactif.'
    },
    {
        icon: 'fal fa-paperclip',
        title: 'Pièces jointes',
        description:
            'Taille max par fichier définie par le formulaire. Plusieurs fichiers peuvent être ajoutés sur un même champ upload.'
    }
]

help.CreateApplications = [
    {
        icon: 'fal fa-plug',
        title: 'Déployer le formulaire',
        description:
            'Un déploiement rend le formulaire disponible sur un canal donné (site web, application mobile). Plusieurs déploiements peuvent coexister pour un même formulaire.'
    },
    {
        icon: 'fal fa-link',
        title: 'Client URL et source',
        description:
            'La « Client URL » est l’adresse du portail qui affichera le formulaire ; la « source » identifie le canal dans les statistiques et sur les tickets reçus.'
    },
    {
        icon: 'fal fa-toggle-on',
        title: 'Langue et disponibilité',
        description:
            'Un déploiement inactif cesse immédiatement d’accepter de nouvelles demandes, sans supprimer les tickets déjà reçus.'
    }
]

help.Page403 = [
    {
        icon: 'fal fa-shield-alt',
        title: 'Accès refusé',
        description:
            'Votre rôle ne dispose pas de la permission nécessaire pour cette page. Demandez à un administrateur de l’ajouter depuis la page Rôles.'
    },
    {
        icon: 'fal fa-sync',
        title: 'Permission ajoutée récemment ?',
        description:
            'Les droits sont relus au chargement de l’application : rechargez la page pour que la modification prenne effet.'
    }
]

help.Page404 = [
    {
        icon: 'fal fa-compass',
        title: 'Page introuvable',
        description:
            'Le lien est peut-être obsolète ou l’élément a été supprimé. Repassez par la sidebar pour retrouver le module concerné.'
    }
]

help.PageNotFound = help.Page404

// --- Aliases: variant routes share the same content as their base route ---
help.PostsByFormType = help.Data
help.KanbanByFormType = help.Kanban
help.TicketMapByFormType = help.TicketMap
help.PerformancesByFormType = help.Performances
help.CustomFormBuilderEdit = help.CustomFormBuilder
help.CreateCompanyUser = help.CompanyUsers
help.EditCompanyUser = help.CompanyUsers
help.CreateCompanyWebhook = help.CompanyWebhooks
help.EditCompanyWebhook = help.CompanyWebhooks
help.CreateCompany = help.AdminCompanies
help.Company = help.AdminCompanies
help.EditCompany = help.AdminCompanies
help.EditApplications = help.CreateApplications
help.EventEdit = help.EventCreate

export default help
